'use strict';

/**
 * Admin Pipeline Status mock showcase at `/home/pipeline-tracker`.
 *
 * Top tabs pick a pipeline group; second-row tabs pick a mock project. The panel
 * below is the same tracker surface as `/home/project/<code>/home` (fixtures only —
 * no live PortalApi, no global RTK mock interceptor).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import {
	Alert,
	Box,
	Tab,
	Tabs,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { NavBar, NavButton } from '../../components/index.jsx';
import ManagementPageCard from '../../components/layout/ManagementPageCard.jsx';
import { ENABLE_PIPELINE_TRACKER } from '@/config/env.config';
import {
	PIPELINE_STATUS_SHOWCASE_GROUPS,
	showcaseScenariosForGroup,
} from '@/features/pipelineStatus/pipelineStatusShowcaseCatalog';
import { PipelineStatusShowcaseCard } from '@/features/pipelineStatus/PipelineStatusShowcaseCard';
import { buildShowcaseActivityRows } from '@/features/pipelineStatus/buildShowcaseActivityRows';
import { ActivityStripView } from '@/components/layout/ActivityStrip.jsx';

function a11yProps(prefix, index) {
	return {
		id: `${prefix}-tab-${index}`,
		'aria-controls': `${prefix}-tabpanel-${index}`,
	};
}

function PipelineTrackerShowcaseBody() {
	const theme = useTheme();
	const narrow = useMediaQuery(theme.breakpoints.down('md'));
	const [groupTab, setGroupTab] = useState(0);
	const [projectTab, setProjectTab] = useState(0);

	const groups = PIPELINE_STATUS_SHOWCASE_GROUPS;
	const activeGroup = groups[groupTab] ?? groups[0];
	const scenarios = useMemo(
		() => showcaseScenariosForGroup(activeGroup.id),
		[activeGroup.id],
	);
	const activeScenario = scenarios[projectTab] ?? scenarios[0];
	const tickerItems = useMemo(
		() => buildShowcaseActivityRows(activeScenario),
		[activeScenario],
	);

	useEffect(() => {
		setProjectTab(0);
	}, [activeGroup.id]);

	if (!ENABLE_PIPELINE_TRACKER) {
		return (
			<Alert severity="warning" data-testid="pipeline-showcase-disabled">
				Pipeline Status preview requires <code>ENABLE_PIPELINE_TRACKER</code>.
				This page never calls the live Pipeline Status API.
			</Alert>
		);
	}

	return (
		<Box sx={{ width: '100%', minWidth: 0 }} data-testid="pipeline-status-showcase">
			<Alert
				severity="info"
				sx={{ mb: 2 }}
				data-testid="pipeline-showcase-mock-banner"
			>
				<strong>Pipeline Status preview — mock data</strong>
				<br />
				Each tab is a simulated project. The panel below is what you would see on that
				project&apos;s Home page. The scrolling bar is the header ticker tape, fed from
				this mock project&apos;s last-hour facts. Nothing here calls the live Pipeline
				Status API.
			</Alert>

			<Box sx={{ mb: 2, mx: { xs: -1.5, md: -2 } }} data-testid="pipeline-showcase-ticker">
				<ActivityStripView
					items={tickerItems}
					ariaLabel="Mock last-hour pipeline activity ticker"
				/>
			</Box>

			<Tabs
				value={groupTab}
				onChange={(_e, next) => {
					setGroupTab(next);
					setProjectTab(0);
				}}
				variant={narrow ? 'scrollable' : 'standard'}
				scrollButtons={narrow ? 'auto' : false}
				aria-label="Pipeline type"
				sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
			>
				{groups.map((g, index) => (
					<Tab key={g.id} label={g.label} {...a11yProps('pipeline-showcase-group', index)} />
				))}
			</Tabs>

			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				{activeGroup.description}
			</Typography>

			<Tabs
				value={Math.min(projectTab, Math.max(scenarios.length - 1, 0))}
				onChange={(_e, next) => setProjectTab(next)}
				variant="scrollable"
				scrollButtons="auto"
				aria-label="Mock projects"
				sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
				data-testid="pipeline-showcase-project-tabs"
			>
				{scenarios.map((scenario, index) => (
					<Tab
						key={scenario.id}
						label={scenario.label}
						{...a11yProps('pipeline-showcase-project', index)}
					/>
				))}
			</Tabs>

			<Box
				role="tabpanel"
				id={`pipeline-showcase-project-tabpanel-${projectTab}`}
				aria-labelledby={`pipeline-showcase-project-tab-${projectTab}`}
				data-testid="pipeline-showcase-project-panel"
			>
				{activeScenario && <PipelineStatusShowcaseCard scenario={activeScenario} />}
			</Box>
		</Box>
	);
}

export function PipelineTrackerPage(props) {
	const { match, history } = props;
	return (
		<ManagementPageCard
			className="app-content"
			header={(
				<NavBar history={history} controlled={true} size="small">
					<NavButton to={match.url}>
						<span style={{ fontSize: '1.125rem', fontWeight: 500 }}>Pipeline Tracker</span>
					</NavButton>
				</NavBar>
			)}
		>
			{/* Avoid class "main": .HomeContainer .main applies display:flex and breaks nested layout */}
			<Box sx={{ width: '100%', minWidth: 0, display: 'block', p: { xs: 1.5, md: 2 } }}>
				<PipelineTrackerShowcaseBody />
			</Box>
		</ManagementPageCard>
	);
}

export default withRouter(PipelineTrackerPage);
